import { useState } from 'react';
import { Document, Page } from 'react-pdf';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { WebinarSlide } from '@/features/webinar/types';
import type { LiveSessionResourceStateDto, TriggerEventInput } from '@/features/live/types';
import { getStoredAdminPdfPage } from '@/lib/liveAdminPdfPageMemory';

interface SlidesListProps {
  slides: WebinarSlide[];
  loading?: boolean;
  sessionId: number;
  adminResourceStates: LiveSessionResourceStateDto[];
  handleTriggerEvent: (event: TriggerEventInput) => void;
  isModeratorLive?: boolean;
}

export default function SlidesList({
  slides,
  loading = false,
  sessionId,
  adminResourceStates,
  handleTriggerEvent,
  isModeratorLive = false,
}: SlidesListProps) {
  const { t } = useTranslation('webinars');
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const [totalPages, setTotalPages] = useState<Record<number, number>>({});

  const handleDocumentLoad =
    (slideId: number) =>
    ({ numPages }: { numPages: number }) => {
      setTotalPages((prev) => ({
        ...prev,
        [slideId]: numPages,
      }));
    };

  const handleToggleAssetLive = (slide: WebinarSlide) => {
    const isSlideLive = adminResourceStates.some(
      (state) =>
        state.resourceType === 'WEBINAR_SLIDE' && state.resourceId === slide.id && state.live,
    );
    if (isSlideLive) {
      handleTriggerEvent({
        eventType: 'RESOURCE_HIDE',
        resourceType: 'WEBINAR_SLIDE',
        resourceId: slide.id,
        payloadJson: JSON.stringify({
          timeNow: new Date().getTime(),
        }),
      });
    } else {
      const resumePage = getStoredAdminPdfPage(sessionId, slide.id) ?? 1;
      handleTriggerEvent({
        eventType: 'RESOURCE_SHOW',
        resourceType: 'WEBINAR_SLIDE',
        resourceId: slide.id,
        payloadJson: JSON.stringify({
          timeNow: new Date().getTime(),
          pageNumber: resumePage,
        }),
      });
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto">
        <Skeleton className="w-[230px] h-[128px] rounded-2xl shrink-0" />
        <Skeleton className="w-[230px] h-[128px] rounded-2xl shrink-0" />
        <Skeleton className="w-[230px] h-[128px] rounded-2xl shrink-0" />
      </div>
    );
  }

  if (!slides || slides.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        <p>{t('adminLive.slides.empty')}</p>
      </div>
    );
  }

  return (
    <div
      className={`flex gap-4 ${isModeratorLive ? 'flex-row flex-wrap' : 'overflow-x-auto'}`}
      style={{ scrollbarColor: 'currentColor transparent' }}
    >
      {slides.map((slide) => {
        const isSlideLive = adminResourceStates.some(
          (state) =>
            state.resourceType === 'WEBINAR_SLIDE' && state.resourceId === slide.id && state.live,
        );
        return (
          <div
            key={slide.id}
            className="shrink-0 w-[230px] h-[128px] bg-white rounded-2xl flex flex-col items-center justify-center relative"
          >
            <div className="w-[230px] h-[128px] flex flex-col items-center justify-center overflow-hidden rounded-2xl">
              <Document
                file={slide.slideUrl}
                onLoadSuccess={handleDocumentLoad(slide.id)}
                className="max-w-[230px] max-h-[128px]"
                loading={<Skeleton className="w-[230px] h-[128px] rounded-2xl" />}
                error={
                  <div className="p-4 text-red-600 text-sm w-[230px] h-[128px] rounded-2xl">
                    {t('adminLive.pdf.loadError')}
                  </div>
                }
              >
                <Page
                  pageIndex={0}
                  width={230}
                  height={128}
                  className="rounded-2xl"
                  error={
                    <div className="p-4 text-red-600 text-sm">{t('adminLive.pdf.loadError')}</div>
                  }
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={<Skeleton className="w-[230px] h-[128px] rounded-2xl" />}
                />
              </Document>
            </div>

            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-100">
              {isSlideLive ? (
                <Button
                  className="bg-[#FFD753] hover:bg-[#FFD753]/80"
                  onClick={() => handleToggleAssetLive(slide)}
                >
                  {t('adminLive.cards.stop')}
                </Button>
              ) : (
                <Button
                  onClick={() => handleToggleAssetLive(slide)}
                  style={{
                    backgroundColor:
                      currentWorkspace?.visualIdentity?.secondaryBackgroundColor || undefined,
                    color:
                      currentWorkspace?.visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
                  }}
                >
                  {t('adminLive.cards.start')}
                </Button>
              )}
            </div>

            <div className="absolute bottom-0 left-0 py-1 px-3 w-full bg-black rounded-b-2xl rounded-tr-none">
              <p className="text-white text-xs" style={{ wordBreak: 'break-word' }}>
                {slide.fileName} -{' '}
                <span className="text-text-secondary">
                  {t('adminLive.slides.pageCount', {
                    count: totalPages[slide.id] || slide.pageCount || 0,
                  })}
                </span>
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
